/**
 * @file Shared "a device an employee holds" row renderer.
 *
 * Both /me (employee view) and /people/:id (admin view) render a list of
 * devices that belong to one person. The structure is identical: category
 * icon, name + meta line, status badge, context-tag chips. The two pages
 * used to maintain near-identical copies — kept here so they can't drift.
 *
 * Mode flag:
 *   { admin: false }  — /me. No status badge, no reconcile chips, no
 *                       inline diagnostic about Meraki / Abra disagreements.
 *                       Employees shouldn't see source-attribution.
 *   { admin: true }   — /people/:id. Status badge + reconcile chips +
 *                       inline mismatch hint. Clickable row → /devices/:id.
 *
 * Reconcile chips read from `device.match.status` (set by materialise):
 *   orphan_no_abra   → red "Not in Abra"
 *   holder_mismatch  → yellow "Holder mismatch"
 *   abra_unassigned  → blue "From Meraki"
 *   matched / null   → no chip
 */

import { el } from '../utils.js';
import { copyable } from './copyable.js';
import { categoryIcon } from '../icons.js';
import { CATEGORIES, DEVICE_STATUSES, contextTagInfo } from '../config.js';

/**
 * @param device                  Device doc from /devices or /device_view.
 * @param opts.admin              Boolean — admin view (defaults to false).
 * @param opts.viewingEmployeeId  Admin only — the employee whose page this is.
 *                                Used to tailor the mismatch hint to "this
 *                                side vs the other side".
 * @param opts.onClick            Optional click handler (typically navigate
 *                                to /devices/:id).
 */
export function renderHeldDeviceRow(device, opts = {}) {
  const { admin = false, viewingEmployeeId = null, onClick = null } = opts;
  const cat = CATEGORIES[device.category] || { label: device.category || 'Item' };

  // Context-tag chips (Testing, Lab, Storage, etc.) on both views.
  const chips = (device.contextTags || []).map((t) => {
    const info = contextTagInfo(t);
    return el('span', { class: `chip ${info.cls || ''}` }, info.label);
  });

  // Reconcile chip — admin only. /me deliberately hides source attribution.
  const matchStatus = device.match?.status || null;
  const isOrphan   = matchStatus === 'orphan_no_abra';
  const isMismatch = matchStatus === 'holder_mismatch';
  const isFromMdm  = matchStatus === 'abra_unassigned';
  // The MDM side — Meraki or IRU (both run during the IRU rollout). Label
  // tracks whichever system carries this device.
  const mdm = device.meraki || device.iru || null;
  const mdmLabel = device.meraki ? 'Meraki' : (device.iru ? 'IRU' : 'MDM');
  if (admin) {
    if (isOrphan)        chips.push(el('span', { class: 'chip chip-warning' }, 'Not in Abra'));
    else if (isMismatch) chips.push(el('span', { class: 'chip chip-mismatch' }, 'Holder mismatch'));
    else if (isFromMdm)  chips.push(el('span', { class: 'chip chip-info' }, `From ${mdmLabel}`));
  }

  // Meta line content. Phone tariffs use the name as the phone number and
  // get a category-label hint instead of an SN.
  const meta = [];
  if (device.category === 'phone_tariff') {
    meta.push(cat.label);
  }
  if (device.sn) {
    // Full SN — both employees and admins. No obfuscation; shoulder-surfing
    // isn't a meaningful threat for serial numbers on an internal tool.
    meta.push(copyable(device.sn, { label: 'SN', mono: true, style: 'margin-right:12px', display: `SN ${device.sn}` }));
  }
  // Admin-only mismatch hint inline on the meta line.
  if (admin && isMismatch) {
    const abraEmail = device.abra?.holderEmail || '(unassigned in Abra)';
    const mdmEmail  = mdm?.ownerEmail || '?';
    if (viewingEmployeeId && device.abra?.holderEmployeeId === viewingEmployeeId
        && mdm?.ownerEmployeeId && mdm.ownerEmployeeId !== viewingEmployeeId) {
      meta.push(el('span', { class: 'meta-mismatch', style: 'margin-left:12px' },
        `Abra has it assigned here, but ${mdmLabel} says ${mdmEmail} holds it`));
    } else if (viewingEmployeeId && mdm?.ownerEmployeeId === viewingEmployeeId
        && device.abra?.holderEmployeeId && device.abra.holderEmployeeId !== viewingEmployeeId) {
      meta.push(el('span', { class: 'meta-mismatch', style: 'margin-left:12px' },
        `${mdmLabel} has it assigned here, but Abra still lists ${abraEmail}`));
    } else {
      meta.push(el('span', { class: 'meta-mismatch', style: 'margin-left:12px' },
        `Abra: ${abraEmail} · ${mdmLabel}: ${mdmEmail} — usually ${mdmLabel} is right, reconcile in Abra`));
    }
  } else if (admin && isOrphan) {
    meta.push(el('span', { class: 'meta-warning', style: 'margin-left:12px' }, `${mdmLabel} only — not yet in Abra`));
  } else if (admin && isFromMdm) {
    meta.push(el('span', { class: 'meta-info', style: 'margin-left:12px' },
      `Holder sourced from ${mdmLabel} — Abra has no assignment for this device`));
  }

  // Status badge — admin only. /me intentionally omits it; the
  // employee-facing surface should not carry "in_use" / "retired" pills.
  const status = admin ? (DEVICE_STATUSES[device.status] || { label: device.status, badge: 'idle' }) : null;

  const rowClass = `item${admin && isOrphan ? ' item-alert' : ''}${admin && isMismatch ? ' item-warn' : ''}`;
  const rowAttrs = { class: rowClass };
  if (onClick) {
    rowAttrs.style = 'cursor:pointer';
    rowAttrs.onClick = onClick;
    rowAttrs['data-row-link'] = true; // touch: a tap on the name opens, not copies
  }

  return el('div', rowAttrs,
    el('span', { class: 'cat-icon' }, categoryIcon(device.category)),
    el('div', null,
      // Name copies (for phone tariffs the name IS the number). SN copies
      // the raw serial while displaying "SN …". Both stop propagation so the
      // row's own click (→ device page) still works everywhere else.
      el('div', { class: 'name' }, device.name
        ? copyable(device.name, { label: device.category === 'phone_tariff' ? 'number' : 'name' })
        : cat.label),
      el('div', { class: 'meta' }, ...interleaveSeparators(meta)),
    ),
    admin ? el('span', { class: `badge badge-${status.badge}` }, status.label) : el('span'),
    el('div', { class: 'chips' }, ...chips),
  );
}

function interleaveSeparators(parts) {
  const out = [];
  parts.forEach((p, i) => {
    if (i > 0) out.push(el('span', { class: 'muted' }, ' · '));
    out.push(p);
  });
  return out;
}
